import React, { useMemo } from "react";
import { Box, Flex, Text, Spinner } from "@radix-ui/themes";
import {
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  ReferenceLine,
  Line,
  LineChart,
} from "recharts";
import { format } from "date-fns";

interface TimeseriesDataPoint {
  datetime: string;
  price: number;
  percent_move: number;
}

interface MiniPerformanceChartProps {
  timeseries?: TimeseriesDataPoint[];
  ticker?: string;
  timeframe?: string;
  height?: number;
  isLoading?: boolean;
}

const formatPercentChange = (value: number): string => {
  const sign = value >= 0 ? "+" : "";
  return `${sign}${value.toFixed(2)}%`;
};

interface TooltipData {
  price: number;
  percent_move: number;
  datetime: string;
}

interface TooltipPayload {
  payload: TooltipData;
}

const CustomTooltip = ({
  active,
  payload,
}: {
  active?: boolean;
  payload?: TooltipPayload[];
}) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    const percentChange = data.percent_move;
    const isPositive = percentChange >= 0;

    return (
      <Box
        style={{
          backgroundColor: "var(--color-panel)",
          border: "1px solid var(--gray-6)",
          borderRadius: "4px",
          padding: "8px",
        }}
      >
        <Flex direction="column" gap="1">
          <Text size="1" color={isPositive ? "green" : "red"} weight="medium">
            {formatPercentChange(percentChange)}
          </Text>
          <Text size="1" color="gray">
            {format(new Date(data.datetime), "MMM d")}
          </Text>
        </Flex>
      </Box>
    );
  }
  return null;
};

/**
 * Mini chart that shows percent change performance over time
 * Does not show actual prices - only percent movements
 */
export default function MiniPerformanceChart({
  timeseries = [],
  ticker,
  timeframe = "1M",
  height = 160,
  isLoading = false,
}: MiniPerformanceChartProps) {
  // Calculate cumulative percent changes from first data point
  const chartData = useMemo(() => {
    if (!timeseries || timeseries.length === 0) return [];

    // Sort by date to ensure proper ordering
    const sorted = [...timeseries].sort(
      (a, b) => new Date(a.datetime).getTime() - new Date(b.datetime).getTime()
    );

    // Find the first non-zero price to use as baseline
    const startPrice = sorted.find((point) => point.price > 0)?.price;

    // If no valid start price, return empty array
    if (!startPrice || startPrice === 0) return [];

    // Filter out entries with zero prices and calculate percent moves
    return sorted
      .filter((point) => point.price > 0)
      .map((point) => ({
        datetime: point.datetime,
        price: point.price,
        // Calculate percent change from start
        percent_move: ((point.price - startPrice) / startPrice) * 100,
      }));
  }, [timeseries]);

  // Calculate tick positions for x-axis (show ~3 ticks for 1M)
  const xAxisTicks = useMemo(() => {
    if (timeframe !== "1M" || chartData.length === 0) return undefined;

    const numTicks = 3;
    const step = Math.floor(chartData.length / (numTicks + 1));
    const ticks: string[] = [];

    for (let i = 1; i <= numTicks; i++) {
      const index = Math.min(i * step, chartData.length - 1);
      ticks.push(chartData[index].datetime);
    }

    return ticks;
  }, [chartData, timeframe]);

  // Format tick labels for x-axis
  const formatXAxisTick = (datetime: string) => {
    return format(new Date(datetime), "MMM d");
  };

  // Determine if overall trend is positive
  const isPositiveTrend = useMemo(() => {
    if (chartData.length === 0) return true;
    const lastPoint = chartData[chartData.length - 1];
    return lastPoint.percent_move >= 0;
  }, [chartData]);

  if (isLoading) {
    return (
      <Flex align="center" justify="center" height={`${height}px`} width="100%">
        <Spinner size="1" />
      </Flex>
    );
  }

  if (chartData.length === 0) {
    return (
      <Flex align="center" justify="center" height={`${height}px`} width="100%">
        <Text size="1" color="gray">
          No data
        </Text>
      </Flex>
    );
  }

  // Calculate Y-axis domain
  const percentMoves = chartData.map((d) => d.percent_move);
  const minPercent = Math.min(...percentMoves, 0); // Include 0 baseline
  const maxPercent = Math.max(...percentMoves, 0);

  const color = isPositiveTrend ? "#569346" : "#DC2626";

  return (
    <Box>
      {ticker && (
        <Flex justify="between" align="center" mb="1">
          <Text size="1" weight="medium" color="gray">
            {ticker}
          </Text>
          <Text size="1" color="gray">
            {timeframe}
          </Text>
        </Flex>
      )}

      <Box height={`${height}px`} width="100%" position="relative">
        <ResponsiveContainer width="100%" height={height}>
          <LineChart
            data={chartData}
            margin={{ top: 5, right: 5, left: 5, bottom: 5 }}
          >
            <defs>
              <linearGradient
                id={`miniPerfGradient-${ticker || "chart"}`}
                x1="0"
                y1="0"
                x2="0"
                y2="1"
              >
                <stop offset="0%" stopColor={color} />
                <stop offset="100%" stopColor={color} />
              </linearGradient>
            </defs>

            <XAxis
              dataKey="datetime"
              axisLine={false}
              tickLine={false}
              tick={
                timeframe === "1M"
                  ? { fontSize: 11, fill: "var(--gray-9)" }
                  : false
              }
              ticks={xAxisTicks}
              tickFormatter={formatXAxisTick}
              height={timeframe === "1M" ? 20 : 0}
            />
            <YAxis hide domain={[minPercent, maxPercent]} />
            <Tooltip content={<CustomTooltip />} />

            {/* Zero baseline */}
            <ReferenceLine
              y={0}
              stroke="var(--gray-9)"
              strokeDasharray="4 4"
              strokeWidth={1}
              opacity={0.6}
            />

            <Line
              type="monotone"
              dataKey="percent_move"
              stroke={`url(#miniPerfGradient-${ticker || "chart"})`}
              strokeWidth={2}
              dot={false}
              connectNulls={true}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
}
